// src/layout/Navbar.tsx
import {
  Link,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useMemo, useState, useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import { Input, Badge, Dropdown } from "@/ui";
import {
  ShoppingCart,
  Search,
  Globe,
  ChevronDown,
  MapPin,
  Menu,
  Heart,
} from "lucide-react";
import Container from "./Container";
import { useCartStore } from "@/features/cart/store";
import { useAuth } from "@/hooks/useAuth";
import { useCustomerTier } from "@/features/account/hooks/useCustomerTier";
import ThemeToggle from "@/theme/ThemeToggle";
import { useCurrency } from "@/features/currency/CurrencyProvider";
import { Price } from "@/features/currency/Price";
import SearchBox from "@/features/Search/components/SearchBox";
import { NotificationDropdown } from "@/features/notifications/components/NotificationDropdown";
import { fetchWishlist, type WishlistItemDTO } from "@/features/wishlist/api";
import { fetchCategories, type CategoryNode } from "@/features/categories/api";
import { resolveStorefrontOrgSlug } from "@/lib/api";
import { buildStorePathFor } from "@/lib/product-path";
import {
  useShowRatings,
  useStorefrontConfig,
} from "@/features/checkout/api/useStorefrontConfig";
import { HEADER_OFFSET_VAR, headerOffsetPx } from "@/lib/header-offset";

/* Utils */
function initials(name?: string | null, email?: string | null) {
  if (name && name.trim()) {
    const p = name.trim().split(/\s+/);
    return ((p[0]?.[0] || "") + (p[1]?.[0] || "")).toUpperCase();
  }
  return (email?.[0] || "U").toUpperCase();
}

function extractOrgSlugFromPath(pathname: string): string | null {
  const match = pathname.match(/^\/s\/([^/]+)/i);
  return match?.[1] ? decodeURIComponent(match[1]).trim().toLowerCase() : null;
}

type Cat = CategoryNode;

type StorefrontUser = {
  id?: string;
  email?: string | null;
  name?: string | null;
  fullName?: string | null;
  avatarUrl?: string | null;
};

function getDisplayName(user?: StorefrontUser | null) {
  return user?.fullName || user?.name || user?.email || "usuario";
}

/* Language switcher */
function LangSwitcher() {
  const [lang, setLang] = useState<string>(() => {
    try {
      return localStorage.getItem("lang") || "ES";
    } catch {
      return "ES";
    }
  });

  function change(next: string) {
    setLang(next);
    try {
      localStorage.setItem("lang", next);
    } catch {
      // no-op
    }
  }

  return (
    <Dropdown
      trigger={
        <button
          aria-label="Idioma"
          title="Idioma"
          className="
            inline-flex items-center gap-1 rounded-lg px-2 py-1
            text-xs md:text-sm transition-colors
            border bg-[rgb(var(--card-2-rgb))] border-[rgb(var(--border-rgb))]
            hover:bg-[rgb(var(--muted-rgb))]
          "
        >
          <Globe size={16} className="opacity-80" />
          <span className="hidden sm:inline">{lang}</span>
        </button>
      }
      items={[
        { label: "ES · Español", onSelect: () => change("ES") },
        { label: "EN · English", onSelect: () => change("EN") },
      ]}
    />
  );
}

/* Currency switcher — opciones reales del tenant (enabledCurrencies);
 * si la tienda es mono-moneda no se renderiza el switcher. */
function CurrencySwitcher() {
  const { currency, setCurrency, enabledCurrencies, rates } = useCurrency();

  if (enabledCurrencies.length <= 1) return null;

  const preview =
    rates?.USD && currency !== "USD"
      ? (() => {
          if (currency === "CUP") {
            const v = Math.round((rates.USD || 0) * 100) / 100;
            return `· 1 USD≈${v} MN`;
          }
          const denom = (rates as any)?.[currency];
          if (!denom) return "";
          const v = Math.round(((rates.USD || 0) / denom) * 100) / 100;
          return `· 1 USD≈${v} ${currency}`;
        })()
      : "";

  const items = enabledCurrencies.map((c) => {
    const label =
      c === "CUP"
        ? "CUP (MN)"
        : (rates as any)?.[c]
          ? `${c} · ${(rates as any)[c]} MN`
          : c;

    return { label, onSelect: () => setCurrency(c) };
  });

  return (
    <Dropdown
      trigger={
        <button
          aria-label="Moneda"
          title={`Moneda: ${currency}`}
          className="
            inline-flex items-center gap-2 rounded-lg px-2 py-1
            text-xs md:text-sm transition-colors
            border bg-[rgb(var(--card-2-rgb))] border-[rgb(var(--border-rgb))]
            hover:bg-[rgb(var(--muted-rgb))]
          "
        >
          <span className="font-semibold">{currency}</span>
          <span className="text-[10px] md:text-xs opacity-70 hidden md:inline">
            {preview}
          </span>
        </button>
      }
      items={items}
    />
  );
}

/* Navbar */
export default function Navbar() {
  const nav = useNavigate();
  const loc = useLocation();
  const [sp] = useSearchParams();

  const orgSlug =
    extractOrgSlugFromPath(loc.pathname) || resolveStorefrontOrgSlug();

  // Slice 11c — mismo helper que el resto de la tienda (buildStorePath), con
  // el slug de la URL actual primero.
  const storePath = (suffix = "") => buildStorePathFor(orgSlug, suffix);

  // Slice 11 — nombre público del negocio y mensaje de la barra superior.
  const { data: storefrontConfig } = useStorefrontConfig();
  const showRatings = useShowRatings();
  const storeName = storefrontConfig?.storeName ?? "";
  const topBarMessage = storefrontConfig?.trust.topBarMessage ?? null;

  // Estado compartido de búsqueda (desktop + móvil)
  const [term, setTerm] = useState("");
  const [cat, setCat] = useState<string>("all");

  useEffect(() => {
    setTerm(sp.get("q") || "");
    setCat(sp.get("cat") || "all");
  }, [sp]);

  function goToSearch(params?: { q?: string; cat?: string }) {
    const qRaw = (params?.q ?? term).trim();
    const c = params?.cat ?? cat;

    const next = new URLSearchParams();
    if (qRaw) next.set("q", qRaw);
    if (c && c !== "all") next.set("cat", c);
    next.set("page", "1");

    nav(`${storePath("/search")}?${next.toString()}`);
  }

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    goToSearch({});
  }

  // Cart
  const items = useCartStore((s) => s.items);
  const count = useMemo(() => items.reduce((a, b) => a + b.qty, 0), [items]);
  // Slice 13b — el total está en la moneda de los ítems (la de la tienda), no
  // en USD: antes se convertía como si fuera USD.
  const totalCents = useMemo(
    () => items.reduce((a, b) => a + b.price * b.qty, 0),
    [items],
  );

  // Animación del carrito cuando cambia el número de ítems
  const [cartBump, setCartBump] = useState(false);
  const lastCount = useRef(count);

  useEffect(() => {
    if (count > lastCount.current) {
      setCartBump(true);
      const t = setTimeout(() => setCartBump(false), 350);
      return () => clearTimeout(t);
    }
    lastCount.current = count;
  }, [count]);

  const { storeCurrency } = useCurrency();
  const cartCurrency = items[0]?.currency || storeCurrency;

  // Auth
  const { user, logout, accessToken } = useAuth();
  const storefrontUser = (user ?? null) as StorefrontUser | null;
  const userName = getDisplayName(storefrontUser);
  const userEmail = storefrontUser?.email ?? null;

  // Panel Mayorista Slice B — tier del customer para condicionar la
  // entrada al panel /wholesale en el dropdown. Hook deshabilita el query
  // si no hay login; cero requests para guests.
  const { isWholesale, wholesaleEnabled } = useCustomerTier();

  const [avatarError, setAvatarError] = useState(false);
  const avatarUrl = storefrontUser?.avatarUrl ?? undefined;

  useEffect(() => {
    setAvatarError(false);
  }, [avatarUrl]);

  // Categorías
  const {
    data: categories,
    isLoading: catsLoading,
    isError: catsError,
  } = useQuery<Cat[]>({
    queryKey: ["categories", orgSlug],
    queryFn: fetchCategories,
    staleTime: 5 * 60_000,
  });

  const safeCategories = Array.isArray(categories) ? categories : [];
  const parentCats = useMemo(
    () => safeCategories.filter((c) => c.slug !== "all"),
    [safeCategories],
  );

  const isAuthed = Boolean(accessToken && user);

  // Wishlist: solo fetch si autenticado — en guest mode count=0
  const { data: wishlistItems } = useQuery<WishlistItemDTO[]>({
    queryKey: ["wishlist"],
    enabled: isAuthed,
    queryFn: () => fetchWishlist(accessToken),
    staleTime: 60_000,
  });

  const wishlistCount = wishlistItems?.length ?? 0;
  const hasWishlist = wishlistCount > 0;

  // Animación del corazón cuando aumenta la cantidad de favoritos
  const [favBump, setFavBump] = useState(false);
  const lastWishlistCount = useRef(wishlistCount);

  useEffect(() => {
    if (wishlistCount > lastWishlistCount.current) {
      setFavBump(true);
      const t = setTimeout(() => setFavBump(false), 350);
      return () => clearTimeout(t);
    }
    lastWishlistCount.current = wishlistCount;
  }, [wishlistCount]);

  const userInitials = storefrontUser ? initials(userName, userEmail) : "U";

  const showAvatar = !!(avatarUrl && !avatarError);

  const greeting = storefrontUser
    ? `Hola, ${userName.split(" ")[0] || "usuario"}`
    : "Hola, identifícate";

  // Hide / show navbar según scroll
  const [navHidden, setNavHidden] = useState(false);
  const lastScrollY = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      const current = window.scrollY || 0;
      const prev = lastScrollY.current;
      const diff = current - prev;

      if (Math.abs(diff) < 4) return;

      if (current < 40) {
        setNavHidden(false);
      } else if (diff > 0 && current > 80) {
        setNavHidden(true);
      } else if (diff < 0) {
        setNavHidden(false);
      }

      lastScrollY.current = current;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Slice 13 — publica el alto real del header (--sf-header-offset): el toast
  // se ubica debajo y no encima. Se actualiza al cambiar de tamaño (franja de
  // categorías, mensaje superior, breakpoints) y al ocultarse con el scroll.
  const headerRef = useRef<HTMLElement | null>(null);
  const headerHeight = useRef(0);
  const navHiddenRef = useRef(navHidden);
  navHiddenRef.current = navHidden;
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const root = document.documentElement;
    const publish = () =>
      root.style.setProperty(
        HEADER_OFFSET_VAR,
        headerOffsetPx(headerHeight.current, navHiddenRef.current),
      );
    const observer = new ResizeObserver(([entry]) => {
      headerHeight.current = entry?.contentRect.height ?? el.offsetHeight;
      publish();
    });
    headerHeight.current = el.offsetHeight;
    publish();
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty(HEADER_OFFSET_VAR);
    };
  }, []);
  useEffect(() => {
    document.documentElement.style.setProperty(
      HEADER_OFFSET_VAR,
      headerOffsetPx(headerHeight.current, navHidden),
    );
  }, [navHidden]);

  return (
    <header
      ref={headerRef}
      className={`
        sticky top-0 z-header shadow-sm
        bg-[rgb(var(--card-rgb)/0.9)] backdrop-blur-md
        transition-transform duration-300
        ${navHidden ? "-translate-y-full" : "translate-y-0"}
      `}
    >
      {/* Slice 11 — barra superior solo con el mensaje que configuró el negocio. */}
      {topBarMessage ? (
        <div className="hidden md:block border-b border-[rgb(var(--border-rgb))]">
          <Container className="!max-w-none px-4 lg:px-10 flex items-center text-[11px] lg:text-xs xl:text-[13px] py-1 text-[rgb(var(--fg-rgb)/0.8)]">
            <span className="truncate">{topBarMessage}</span>
          </Container>
        </div>
      ) : null}

      <div className="hidden sm:block border-b border-[rgb(var(--border-rgb))]">
        <Container
          className="
            !max-w-none w-full
            px-3 sm:px-4 md:px-6 lg:px-10 xl:px-14
            grid items-center gap-2 md:gap-3
            grid-cols-[auto_minmax(0,1.6fr)_auto]
            lg:grid-cols-[auto_minmax(0,2fr)_auto]
            xl:grid-cols-[auto_minmax(0,2.3fr)_auto]
            h-16 lg:h-20
          "
        >
          <div className="flex items-center gap-3 min-w-0">
            <Link
              to={storePath("")}
              aria-label="Ir al inicio"
              className="
                font-extrabold tracking-tight text-transparent bg-clip-text
                bg-gradient-to-r from-[var(--brand-start)] to-[var(--brand-end)]
                text-lg md:text-xl xl:text-2xl select-none whitespace-nowrap
              "
            >
              {storeName}
            </Link>

            <button
              type="button"
              onClick={() => nav(storePath("/account/addresses"))}
              className="
                hidden lg:flex items-center gap-1 px-2 py-1 rounded-lg
                hover:bg-[rgb(var(--card-2-rgb))]
                text-xs
              "
            >
              <MapPin size={14} />
              <span className="flex flex-col leading-tight text-left">
                <span className="text-[10px] opacity-60">Enviar a</span>
                <span className="text-[11px] font-medium truncate">
                  tu zona / dirección
                </span>
              </span>
            </button>
          </div>

          <SearchBox
            term={term}
            setTerm={setTerm}
            cat={cat}
            setCat={setCat}
            onSearch={goToSearch}
          />

          <div className="flex items-center gap-2 justify-self-end">
            <div className="hidden lg:flex items-center gap-2">
              <LangSwitcher />
              <CurrencySwitcher />
              <ThemeToggle />
            </div>

            <Dropdown
              trigger={
                <button
                  aria-label="Cuenta"
                  className="
                    hidden md:flex flex-col items-start px-2 py-1 rounded-lg
                    hover:bg-[rgb(var(--card-2-rgb))]
                    text-xs lg:text-[13px] leading-tight
                  "
                  title={userEmail || ""}
                >
                  <span className="opacity-80 text-[11px] lg:text-xs">
                    {greeting}
                  </span>
                  <span className="font-semibold text-[11px] lg:text-xs">
                    Cuenta y listas
                  </span>
                </button>
              }
              items={[
                ...(storefrontUser
                  ? [
                      {
                        label: "Mi cuenta",
                        onSelect: () => nav(storePath("/me")),
                      },
                      {
                        label: "Mis pedidos",
                        onSelect: () => nav(storePath("/orders")),
                      },
                      // Panel Mayorista Slice B — entrada al panel /wholesale.
                      // Visible solo si el dueño habilitó la venta mayorista.
                      // Label cambia según si el customer ya fue aprobado:
                      //   WHOLESALE → "Mi panel mayorista" (lleva al catálogo)
                      //   RETAIL    → "Cuenta mayorista" (lleva al formulario)
                      ...(wholesaleEnabled
                        ? [
                            {
                              label: isWholesale
                                ? "Mi panel mayorista"
                                : "Cuenta mayorista",
                              onSelect: () => nav(storePath("/wholesale")),
                            },
                          ]
                        : []),
                      { label: "Salir", onSelect: logout },
                    ]
                  : [
                      {
                        label: "Entrar",
                        onSelect: () => nav(storePath("/login")),
                      },
                      {
                        label: "Crear cuenta",
                        onSelect: () => nav(storePath("/register")),
                      },
                    ]),
              ]}
            />

            {isAuthed && storefrontUser && (
              <button
                aria-label="Perfil"
                title={userName || userEmail || ""}
                className="
                  hidden lg:inline-flex h-9 w-9 items-center justify-center rounded-full text-sm font-semibold
                  border bg-[rgb(var(--card-2-rgb))] border-[rgb(var(--border-rgb))]
                  overflow-hidden
                "
              >
                {showAvatar ? (
                  <img
                    src={avatarUrl}
                    alt={userName || userEmail || "Perfil"}
                    className="h-full w-full object-cover"
                    loading="lazy"
                    referrerPolicy="no-referrer"
                    onError={() => setAvatarError(true)}
                  />
                ) : (
                  <span className="select-none">{userInitials}</span>
                )}
              </button>
            )}

            {isAuthed && (
              <NotificationDropdown
                accessToken={accessToken}
                onViewAll={() => nav(storePath("/notifications"))}
              />
            )}

            <Link
              to={storePath("/wishlist")}
              aria-label="Ver favoritos"
              className="
                relative inline-flex h-9 w-9 items-center justify-center
                rounded-full hover:bg-[rgb(var(--card-2-rgb))]
                transition-colors
              "
            >
              <Heart
                size={18}
                className={`
                  ${favBump ? "nav-icon-bounce" : ""}
                  ${
                    hasWishlist
                      ? "text-rose-500"
                      : "text-[rgb(var(--fg-rgb)/0.9)]"
                  }
                `}
                fill={hasWishlist ? "currentColor" : "none"}
              />
              {hasWishlist && (
                <span
                  className="
                    absolute -top-1 right-0
                    text-[9px] font-semibold
                    text-[rgb(var(--fg-rgb)/0.85)]
                  "
                >
                  {wishlistCount > 99 ? "99+" : wishlistCount}
                </span>
              )}
            </Link>

            <Link
              to={storePath("/cart")}
              aria-label="Ir al carrito"
              className="
                relative inline-flex items-center gap-1.5 px-2 py-1
                rounded-full hover:bg-[rgb(var(--card-2-rgb))]
                transition-colors
              "
            >
              <div
                className={`
                  relative inline-flex h-9 w-9 items-center justify-center rounded-full
                  ${cartBump ? "nav-icon-bounce" : ""}
                `}
              >
                <ShoppingCart
                  size={18}
                  className="text-[rgb(var(--fg-rgb)/0.95)]"
                />
                {count > 0 && (
                  <span className="absolute -right-1 -top-1">
                    <Badge className="bg-[rgb(var(--primary-rgb))] text-[var(--kw-fg-on-primary)] border-none">
                      {count}
                    </Badge>
                  </span>
                )}
              </div>
              <div className="hidden xl:flex flex-col leading-tight text-xs lg:text-[13px]">
                <span className="text-[rgb(var(--fg-rgb)/0.7)]">Total</span>
                <span className="font-semibold">
                  <Price cents={totalCents} currency={cartCurrency} />
                </span>
              </div>
            </Link>
          </div>
        </Container>

        <Container className="!max-w-none px-3 sm:px-4 md:px-6 lg:px-10 xl:px-14 hidden md:flex items-center gap-4 h-10 lg:h-11 text-[12px] lg:text-[13px] xl:text-sm text-[rgb(var(--fg-rgb)/0.9)]">
          <Dropdown
            trigger={({ open, toggle }) => (
              <button
                type="button"
                onClick={toggle}
                className="
                  inline-flex items-center gap-1.5 rounded-lg px-2 py-1
                  text-xs lg:text-[13px] xl:text-sm
                  border bg-[rgb(var(--card-2-rgb))] border-[rgb(var(--border-rgb))]
                  hover:bg-[rgb(var(--muted-rgb))]
                  font-medium
                "
              >
                <Menu size={14} className="opacity-80" />
                <span>Todas las categorías</span>
                <ChevronDown
                  size={14}
                  className={`opacity-80 transition-transform duration-150 ${
                    open ? "rotate-180" : ""
                  }`}
                />
              </button>
            )}
            items={[
              {
                label: "Todas las categorías",
                onSelect: () => nav(storePath("/search")),
              },
              ...parentCats.flatMap((p) => [
                {
                  label: `${p.name} (todo)`,
                  onSelect: () =>
                    nav(
                      `${storePath("/search")}?cat=${encodeURIComponent(p.slug)}&page=1`,
                    ),
                },
                ...(p.sub || []).map((s) => ({
                  label: `· ${s.name}`,
                  onSelect: () =>
                    nav(
                      `${storePath("/search")}?cat=${encodeURIComponent(s.slug)}&page=1`,
                    ),
                })),
              ]),
            ]}
          />

          <button
            type="button"
            onClick={() =>
              nav(`${storePath("/search")}?sort=createdAt:desc&page=1`)
            }
            className="hover:underline underline-offset-4"
          >
            Novedades
          </button>
          {/* Slice 11c — "Ofertas del día" fuera (no hay sistema de ofertas ni
              ruta); ordena por valoración, así que se llama como lo que hace. */}
          {showRatings ? (
            <button
              type="button"
              onClick={() =>
                nav(`${storePath("/search")}?sort=rating:desc&page=1`)
              }
              className="hover:underline underline-offset-4"
            >
              Mejor valorados
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => nav(storePath("/orders"))}
            className="hover:underline underline-offset-4"
          >
            Mis pedidos
          </button>

          <div className="ml-auto flex items-center gap-2 lg:hidden">
            <LangSwitcher />
            <CurrencySwitcher />
          </div>
        </Container>
      </div>

      <div className="sm:hidden border-b border-[rgb(var(--border-rgb))]">
        <Container
          className="
            !max-w-none px-3 py-2
            flex flex-wrap items-center justify-between
            gap-x-2 gap-y-1
          "
        >
          <div className="flex items-center gap-2 min-w-0">
            <Link
              to={storePath("")}
              aria-label="Ir al inicio"
              className="
                font-extrabold tracking-tight text-transparent bg-clip-text
                bg-gradient-to-r from-[var(--brand-start)] to-[var(--brand-end)]
                text-lg select-none
              "
            >
              {storeName}
            </Link>
            <button
              type="button"
              onClick={() => nav(storePath("/search"))}
              className="
                inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px]
                border bg-[rgb(var(--card-2-rgb))] border-[rgb(var(--border-rgb))]
                hover:bg-[rgb(var(--muted-rgb))]
                font-medium
              "
            >
              <Menu size={14} className="opacity-80" />
              <span>Categorías</span>
            </button>
          </div>

          <div className="flex items-center gap-1.5 flex-shrink-0">
            <LangSwitcher />
            <CurrencySwitcher />
            <ThemeToggle />

            {isAuthed && (
              <NotificationDropdown
                accessToken={accessToken}
                onViewAll={() => nav(storePath("/notifications"))}
              />
            )}

            <Link
              to={storePath("/wishlist")}
              aria-label="Ver favoritos"
              className="
                relative inline-flex h-9 w-9 items-center justify-center
                rounded-full hover:bg-[rgb(var(--card-2-rgb))]
                transition-colors
              "
            >
              <Heart
                size={18}
                className={`
                  ${favBump ? "nav-icon-bounce" : ""}
                  ${
                    hasWishlist
                      ? "text-rose-500"
                      : "text-[rgb(var(--fg-rgb)/0.9)]"
                  }
                `}
                fill={hasWishlist ? "currentColor" : "none"}
              />
              {hasWishlist && (
                <span
                  className="
                    absolute -top-1 right-0
                    text-[9px] font-semibold
                    text-[rgb(var(--fg-rgb)/0.85)]
                  "
                >
                  {wishlistCount > 99 ? "99+" : wishlistCount}
                </span>
              )}
            </Link>

            <Link
              to={storePath("/cart")}
              aria-label="Ir al carrito"
              className="
                relative inline-flex h-9 w-9 items-center justify-center
                rounded-full hover:bg-[rgb(var(--card-2-rgb))]
                transition-colors
              "
            >
              <ShoppingCart
                size={18}
                className={`
                  text-[rgb(var(--fg-rgb)/0.95)]
                  ${cartBump ? "nav-icon-bounce" : ""}
                `}
              />
              {count > 0 && (
                <span className="absolute -right-1 -top-1">
                  <Badge className="bg-[rgb(var(--primary-rgb))] text-[var(--kw-fg-on-primary)] border-none text-[10px]">
                    {count}
                  </Badge>
                </span>
              )}
            </Link>
          </div>
        </Container>

        <Container className="!max-w-none px-3 pb-3 space-y-2">
          <form
            onSubmit={handleSearchSubmit}
            role="search"
            aria-label="Buscar productos"
            className="flex flex-col gap-2"
          >
            <div
              className="
                group flex items-stretch w-full
                rounded-xl overflow-hidden
                bg-[rgb(var(--bg-rgb))]
                border border-[rgb(var(--border-rgb))]
                focus-within:ring-2 focus-within:ring-[rgb(var(--ring-rgb))]
                focus-within:border-transparent
              "
            >
              <div className="relative flex items-center shrink-0 w-[110px] bg-[rgb(var(--card-2-rgb))] border-r border-[rgb(var(--border-rgb))]">
                <label htmlFor="navbar-cat-mobile" className="sr-only">
                  Categoría
                </label>
                <select
                  id="navbar-cat-mobile"
                  name="cat"
                  value={cat}
                  onChange={(e) => setCat(e.target.value)}
                  disabled={catsLoading || catsError}
                  className="
                    w-full h-9
                    pl-2 pr-6
                    text-[11px]
                    bg-transparent border-0
                    outline-none
                    appearance-none
                  "
                >
                  <option value="all">Todas</option>
                  {parentCats.map((p) => (
                    <optgroup key={p.slug} label={p.name}>
                      {(p.sub || []).map((s) => (
                        <option key={s.slug} value={s.slug}>
                          {s.name}
                        </option>
                      ))}
                      <option value={p.slug}>{p.name} (todo)</option>
                    </optgroup>
                  ))}
                </select>
                <span className="pointer-events-none absolute right-1.5 top-1/2 -translate-y-1/2 text-[rgb(var(--fg-rgb)/0.6)]">
                  <ChevronDown size={12} />
                </span>
              </div>

              <div className="relative flex-1 min-w-0">
                <Input
                  id="navbar-q-mobile"
                  name="q"
                  autoComplete="off"
                  placeholder="Buscar productos…"
                  value={term}
                  onChange={(e) => setTerm(e.target.value)}
                  className="
                    h-9
                    border-0
                    rounded-none
                    bg-transparent
                    pl-2 pr-9
                    text-[13px]
                    focus:ring-0 focus:outline-none
                  "
                />
                <button
                  type="submit"
                  aria-label="Buscar"
                  className="
                    absolute right-1 top-1/2 -translate-y-1/2
                    inline-flex h-7 w-7 items-center justify-center
                    rounded-md
                    bg-[rgb(var(--primary-rgb))]
                    text-[var(--kw-fg-on-primary)]
                    hover:bg-[rgb(var(--primary-rgb)/0.9)]
                    text-xs
                  "
                >
                  <Search size={15} />
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between text-[11px] text-[rgb(var(--fg-rgb)/0.8)]">
              <button
                type="button"
                onClick={() => nav(storePath("/orders"))}
                className="hover:underline underline-offset-4"
              >
                Mis pedidos
              </button>
            </div>
          </form>
        </Container>
      </div>
    </header>
  );
}
